import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Loader2, Pencil, Trash2, Shield, Plus } from 'lucide-react';
import AddBusinessRuleForm from '../forms/AddBusinessRuleForm';

interface BusinessRule {
  id: string;
  subcategory_name: string;
  rule_name: string;
  rule_text: string;
  severity: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BusinessRulesProps {
  subcategory: string;
  refreshTrigger?: number;
}

const BusinessRules: React.FC<BusinessRulesProps> = ({ subcategory, refreshTrigger }) => {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canCreate = hasPermission('Field Instructions', 'can_create');
  const canUpdate = hasPermission('Field Instructions', 'can_update');
  const canDelete = hasPermission('Field Instructions', 'can_delete');

  const [rules, setRules] = useState<BusinessRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BusinessRule | null>(null);

  const loadRules = useCallback(async () => {
    if (!subcategory) {
      setRules([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.getBusinessRules(subcategory);
      setRules(res.data || []);
    } catch (err) {
      console.error('Failed to load business rules:', err);
      toast.error('Failed to load business rules', err instanceof Error ? err.message : '');
    } finally {
      setLoading(false);
    }
  }, [subcategory, toast]);

  useEffect(() => {
    loadRules();
  }, [loadRules, refreshTrigger]);

  const handleToggleActive = async (rule: BusinessRule) => {
    if (!canUpdate) return;
    try {
      await api.updateBusinessRule(rule.id, {
        rule_name: rule.rule_name,
        rule_text: rule.rule_text,
        severity: rule.severity,
        is_active: !rule.is_active,
      });
      loadRules();
    } catch (err) {
      toast.error('Failed to update rule', err instanceof Error ? err.message : '');
    }
  };

  const handleDelete = async (rule: BusinessRule) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete business rule "${rule.rule_name}"?`)) return;
    try {
      await api.deleteBusinessRule(rule.id);
      toast.success('Rule deleted', `"${rule.rule_name}" was removed.`);
      loadRules();
    } catch (err) {
      toast.error('Failed to delete rule', err instanceof Error ? err.message : '');
    }
  };

  return (
    <Card className="mt-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-900">Business Rules</h3>
            <span className="text-xs text-gray-500">
              ({rules.length} {rules.length === 1 ? 'rule' : 'rules'})
            </span>
          </div>
          {canCreate && subcategory && (
            <Button
              size="sm"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Business Rule
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Cross-field rules applied to every row during validation (e.g., "If Project = Internal, then Not Billable must be TRUE").
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-6 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading business rules...
          </div>
        ) : rules.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-md">
            No business rules yet. Click "Add Business Rule" to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs">Rule Name</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs">Rule</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs w-20">Severity</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs w-20">Active</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700 text-xs w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rules.map(rule => (
                  <tr key={rule.id} className={rule.is_active ? '' : 'opacity-50'}>
                    <td className="py-2 px-3 align-top font-medium text-gray-900">{rule.rule_name}</td>
                    <td className="py-2 px-3 align-top text-gray-700 whitespace-pre-wrap">{rule.rule_text}</td>
                    <td className="py-2 px-3 align-top">
                      <Badge
                        className={
                          rule.severity === 'Error'
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                        }
                      >
                        {rule.severity}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 align-top">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rule.is_active}
                          onChange={() => handleToggleActive(rule)}
                          disabled={!canUpdate}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </label>
                    </td>
                    <td className="py-2 px-3 align-top">
                      <div className="flex items-center gap-1">
                        {canUpdate && (
                          <button
                            onClick={() => { setEditing(rule); setShowForm(true); }}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5 text-gray-600" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(rule)}
                            className="p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddBusinessRuleForm
          subcategory={subcategory}
          isOpen={showForm}
          editing={editing}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            toast.success(editing ? 'Rule updated' : 'Rule created');
            loadRules();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default BusinessRules;
